import { distractorModeForRating } from "../generate/pickDistractors.js";
import { selectNextQuestionWithGraphAndMastery } from "../graph/graphSelect.js";
import { recommendNextConcept } from "../mastery/recommendConcept.js";
import type { GameMaster, GameMasterId, GameMasterParams } from "./types.js";

const DEFAULT_PARAMS: GameMasterParams = {
  targetElo: 1400,
  masteryBlend: 0.45,
  preferredBand: 300,
  fallbackBand: 2000,
  recencyWindow: 12,
  showStreak: true,
  generatedDistractor: "standard",
};

const PATIENT_PARAMS: GameMasterParams = {
  /** Gentler bar → smaller urgency gaps; more emphasis on graph weights + mastery blend. */
  targetElo: 1200,
  masteryBlend: 0.58,
  preferredBand: 420,
  fallbackBand: 2000,
  recencyWindow: 12,
  showStreak: false,
  generatedDistractor: "far",
  weights: { elo: 0.42, pageRank: 0.38, betweenness: 0.12, topo: 0.08 },
};

const DRILL_PARAMS: GameMasterParams = {
  targetElo: 1550,
  masteryBlend: 0.36,
  preferredBand: 220,
  fallbackBand: 2000,
  recencyWindow: 0,
  showStreak: true,
  generatedDistractor: "near",
  weights: { elo: 0.52, pageRank: 0.28, betweenness: 0.14, topo: 0.06 },
};

function makeGameMaster(id: GameMasterId, params: GameMasterParams): GameMaster {
  return {
    id,
    params,
    pickNextQuestion: ({ bank, player, mastery, scores, excludeIds, pass }) =>
      selectNextQuestionWithGraphAndMastery(bank, player.rating, mastery, scores, {
        preferredBand: pass === "first" ? params.preferredBand : params.fallbackBand,
        excludeIds,
        masteryBlend: params.masteryBlend,
        weights: params.weights,
      }),
    targetEloForRounds: () => params.targetElo,
    recommendConcept: ({ graph, scores, mastery }) =>
      recommendNextConcept(graph, scores, mastery, { targetElo: params.targetElo }),
    framingFor: (view) => {
      if (view !== "home") return null;
      if (id === "patient") return "patient";
      if (id === "drill") return "drill";
      return null;
    },
    distractorForGenerated: (rating) => {
      if (params.generatedDistractor === "standard") {
        return distractorModeForRating(rating);
      }
      return params.generatedDistractor;
    },
  };
}

const BUILT: Record<GameMasterId, GameMaster> = {
  default: makeGameMaster("default", DEFAULT_PARAMS),
  patient: makeGameMaster("patient", PATIENT_PARAMS),
  drill: makeGameMaster("drill", DRILL_PARAMS),
};

export function normalizeGameMasterId(raw: string | undefined | null): GameMasterId {
  if (raw === "default" || raw === "patient" || raw === "drill") return raw;
  return "default";
}

export function getGameMaster(id: GameMasterId): GameMaster {
  return BUILT[id];
}
